import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Modal, Button, Badge } from './Common';
import { AI_MODELS, DEFAULT_SCAN_CONFIG, buildScanPayload } from './agentScanConfig';

/**
 * Launch the AI Pentest Agent against one engagement, from wherever that
 * engagement is already on screen.
 *
 * This is the same launch the Agent Scanner page performs, with the same two
 * gates in front of it, because both exist for a reason:
 *
 *  - Scope is shown before anything is probed. An engagement's scope is copied
 *    from the application when the engagement is created and never re-synced,
 *    so it can still authorise a host that was removed from the application
 *    afterwards. The operator sees exactly what will be hit.
 *  - Destructive testing is off by default. With it on, the agent creates
 *    accounts and writes records on the target and leaves them behind for a
 *    human to clean up.
 *
 * Launching navigates to the Agent Scanner, because a scan is something you
 * watch — the reasoning log, approvals and findings all live there.
 */
export default function AgentScanLaunchModal({ engagement, onClose }) {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();

  const [config, setConfig] = useState(DEFAULT_SCAN_CONFIG);
  const [scope, setScope] = useState({ loading: true, data: null, error: null });
  const [liveSession, setLiveSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));
  const engId = engagement?.id;

  const load = useCallback(async () => {
    if (!engId) return;
    setScope({ loading: true, data: null, error: null });
    try {
      const res = await apiFetch(`/agent-scanner/scope-preview/${engId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setScope({ loading: false, data: null, error: err.error || 'Could not load the engagement scope' });
      } else {
        const data = await res.json();
        setScope({ loading: false, data, error: null });
        // Everything starts ticked: the engagement scope is what this scan is
        // authorised against, and the operator narrows from there. Seeding the
        // ids explicitly also means a scope item added after this point cannot
        // join the scan without being seen.
        setConfig(c => ({ ...c, scopeItemIds: (data.scopeItems || []).map(s => s.id) }));
      }
    } catch {
      setScope({ loading: false, data: null, error: 'Could not load the engagement scope' });
    }

    // A second agent on the same engagement means two scanners hitting one
    // target and duplicate findings. Nothing server-side stops it, so warn.
    try {
      const res = await apiFetch(`/agent-scanner/sessions/${engId}`);
      if (res.ok) {
        const sessions = await res.json();
        setLiveSession((Array.isArray(sessions) ? sessions : []).find(
          s => ['initializing', 'recon', 'scanning', 'paused'].includes(s.status)) || null);
      }
    } catch {}
  }, [engId, apiFetch]);

  useEffect(() => { load(); }, [load]);

  const launch = async () => {
    if (!engId) return;
    setStarting(true);
    setError('');
    try {
      const res = await apiFetch('/agent-scanner/start', {
        method: 'POST',
        body: JSON.stringify(buildScanPayload(engId, config)),
      });
      if (res.ok) {
        onClose?.();
        navigate(`/agent-scanner?engagementId=${engId}`);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to start the scan');
      }
    } catch {
      setError('Failed to start the scan');
    } finally {
      setStarting(false);
    }
  };

  const fieldStyle = {
    padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
    width: '100%',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
  };
  const sectionStyle = {
    fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '18px 0 8px',
  };
  const preTab = (on) => ({
    flex: 1, padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
    fontSize: 12, fontWeight: on ? 600 : 500, fontFamily: 'inherit', textAlign: 'center',
    background: on ? 'var(--primary)' : 'var(--bg-card)',
    color: on ? '#fff' : 'var(--text-secondary)',
    border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
  });
  const linkButtonStyle = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: 'var(--primary, #60a5fa)', fontSize: 11, fontFamily: 'inherit',
  };

  const d = scope.data;
  const stale = d?.drift?.staleInEngagement || [];
  const missing = d?.drift?.missingFromEngagement || [];
  const allItems = d?.scopeItems || [];
  const sel = config.scopeItemIds;
  // Before the preview has loaded there is no selection yet, and reading that
  // as "none selected" would grey out the whole list on the way in.
  const isSelected = id => !Array.isArray(sel) || sel.includes(id);
  const targetCount = allItems.filter(s => isSelected(s.id)).length;
  const toggleScope = id => setConfig(c => {
    const cur = Array.isArray(c.scopeItemIds) ? c.scopeItemIds : allItems.map(s => s.id);
    return { ...c, scopeItemIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
  });
  const setAllScope = on => setConfig(c => ({
    ...c, scopeItemIds: on ? allItems.map(s => s.id) : [],
  }));

  return (
    <Modal isOpen={!!engagement} onClose={onClose} title="Send to AI Pentest Agent" width={660}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
        <strong style={{ color: 'var(--text)' }}>
          {engagement?.name || engagement?.engagement_type}
        </strong>
        {engagement?.client_name ? ` · ${engagement.client_name}` : ''}
        {engagement?.application_name ? ` · ${engagement.application_name}` : ''}
      </div>

      {liveSession && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginTop: 12, fontSize: 12,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b',
        }}>
          &#9888; A scan is already {liveSession.status} on this engagement. Starting another
          points a second agent at the same targets and will duplicate findings.
        </div>
      )}

      {/* Configuration */}
      <div style={sectionStyle}>Configuration</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Mode</label>
          <select value={config.mode} onChange={e => set('mode', e.target.value)} style={fieldStyle}>
            <option value="guided">Guided (Recommended)</option>
            <option value="autonomous">Autonomous</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>AI Model</label>
          <select value={config.aiModel} onChange={e => set('aiModel', e.target.value)} style={fieldStyle}>
            {AI_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
        <div>
          <label style={labelStyle}>Requests</label>
          <input type="number" min="1"
            value={config.unlimitedRequests ? '' : config.budgetRequests}
            disabled={config.unlimitedRequests}
            placeholder={config.unlimitedRequests ? 'unlimited' : ''}
            title={config.unlimitedRequests
              ? 'No request ceiling — the time limit stops the scan'
              : 'Stop after this many HTTP requests'}
            onChange={e => set('budgetRequests', e.target.value)}
            style={{ ...fieldStyle, opacity: config.unlimitedRequests ? 0.5 : 1 }} />
        </div>
        <div>
          <label style={labelStyle}>{config.unlimitedRequests ? 'Hours' : 'Minutes'}</label>
          <input type="number"
            min={config.unlimitedRequests ? '0.5' : '1'}
            step={config.unlimitedRequests ? '0.5' : '5'}
            value={config.unlimitedRequests ? config.budgetHours : config.budgetTime}
            title={config.unlimitedRequests ? 'Stop after this many hours' : 'Stop after this many minutes'}
            onChange={e => set(config.unlimitedRequests ? 'budgetHours' : 'budgetTime', e.target.value)}
            style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Tokens</label>
          <input type="number" min="0" step="100000" value={config.budgetTokens}
            title="Stop after this many model tokens. 0 means no token limit. This agent averages about 7,500 tokens per step."
            onChange={e => set('budgetTokens', e.target.value)} style={fieldStyle} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={config.unlimitedRequests}
            onChange={e => set('unlimitedRequests', e.target.checked)} />
          Unlimited requests &mdash; stop on time instead
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: config.allowDestructive ? '#f59e0b' : 'var(--text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={config.allowDestructive}
            onChange={e => set('allowDestructive', e.target.checked)} />
          Allow destructive tests
        </label>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 22 }}>
          {config.allowDestructive
            ? 'The agent may create accounts and write records on the target, and will leave them behind. A cleanup note is added to the engagement.'
            : 'The agent will not modify the target. Mass assignment and authenticated testing are skipped and reported as not tested.'}
        </div>
        {config.mode === 'guided' && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>&#128737;</span> Guided mode pauses for your approval before: authentication,
            scope expansion, destructive tests, and exploit attempts.
          </div>
        )}
      </div>


      {/* Pre-authentication — a session the tester established themselves */}
      <div style={sectionStyle}>Authentication (optional)</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
        If this application needs a one-time code, SSO or a captcha, the agent cannot
        sign in on its own. Sign in yourself, then paste the session here: it is used
        from the first request, so reconnaissance maps the authenticated surface
        instead of a login page, and the agent never submits the login form.
      </div>
      {config.preAuthKind !== 'token' && (
        <div style={{
          fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8,
          padding: '8px 10px', borderRadius: 6,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
        }}>
          <strong style={{ color: 'var(--text)' }}>You do not pick a cookie — copy the whole header.</strong>
          <div style={{ marginTop: 4 }}>
            In your browser: F12 → <em>Network</em> → click any request made while signed
            in → <em>Headers</em> → under Request Headers copy the entire value of{' '}
            <code>Cookie</code>. It looks like{' '}
            <code>sid=abc; csrf=def; AWSALB=ghi</code>.
          </div>
          <div style={{ marginTop: 4 }}>
            Use Network, not <em>Application &rarr; Cookies</em>: that panel lists them one per
            row, so you would have to assemble the header by hand and decide what to omit — and
            the answer is nothing. An app usually needs several at once: the identity cookie, an
            SSO cookie, a CSRF token, and any bot-protection cookies a WAF set.
          </div>
          <div style={{ marginTop: 4, color: 'var(--warning, #f59e0b)' }}>
            Do not use <code>document.cookie</code> from the console. It cannot see
            HttpOnly cookies, and the ones that authenticate you usually are — you would
            get the harmless cookies, miss the session, and the scan would run anonymous.
          </div>
          <div style={{ marginTop: 4 }}>
            Behind a bot manager (Akamai, Cloudflare, Imperva) those cookies are tied to the
            browser that earned them. Replayed from the scanner they may be rejected whatever
            you paste, which shows up as 403s rather than a login failure.
          </div>
        </div>
      )}
      {(() => {
        const raw = (config.preAuthSecret || '').trim();
        if (!raw) return null;
        if (config.preAuthKind === 'token') return null;
        const body = raw.replace(/^cookie:\s*/i, '');
        const pairs = body.split(';').map(x => x.trim()).filter(x => x.includes('='));
        const names = pairs.map(x => x.split('=')[0].trim()).filter(Boolean);
        const ok = names.length > 0;
        return (
          <div style={{
            fontSize: 11, marginBottom: 8, padding: '6px 10px', borderRadius: 6,
            background: ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
            border: `1px solid ${ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: ok ? '#16a34a' : '#dc2626',
          }}>
            {ok
              ? `${names.length} cookie${names.length === 1 ? '' : 's'} read: ${names.slice(0, 6).join(', ')}${names.length > 6 ? ', …' : ''}`
              : 'No name=value pairs found — this does not look like a Cookie header.'}
            {ok && names.length === 1 && (
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                Only one cookie. If the site set others, the session may not work.
              </div>
            )}
          </div>
        );
      })()}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button type="button" style={preTab(config.preAuthKind !== 'token')}
          onClick={() => set('preAuthKind', 'cookie')}>Cookie header</button>
        <button type="button" style={preTab(config.preAuthKind === 'token')}
          onClick={() => set('preAuthKind', 'token')}>Bearer token</button>
      </div>
      <Input
        label={config.preAuthKind === 'token' ? 'Bearer token' : 'Cookie header'}
        value={config.preAuthSecret || ''}
        onChange={e => set('preAuthSecret', e.target.value)}
        placeholder={config.preAuthKind === 'token'
          ? 'Paste a token, with or without the Bearer prefix'
          : 'name=value; other=value'}
        style={{ marginBottom: 0 }} />
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
        {(config.preAuthSecret || '').trim()
          ? 'Encrypted at rest and used for this scan only. Sessions expire — if it dies mid-scan the agent asks for a new one rather than guessing a password.'
          : 'Leave blank to scan unauthenticated, or to let the agent use credentials stored on the engagement.'}
      </div>

      {/* Scope */}
      <div style={{ ...sectionStyle, display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span>
          {scope.loading ? 'Loading scope…' : `Will be tested (${targetCount} of ${allItems.length})`}
        </span>
        {allItems.length > 1 && (
          <span style={{ fontWeight: 400, fontSize: 11 }}>
            <button type="button" onClick={() => setAllScope(true)} style={linkButtonStyle}>Select all</button>
            <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>·</span>
            <button type="button" onClick={() => setAllScope(false)} style={linkButtonStyle}>Clear</button>
          </span>
        )}
      </div>
      {allItems.length > 1 && !scope.loading && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
          This engagement lists more than one environment. Untick anything the agent must not
          touch &mdash; only ticked targets are authorised, and the agent will refuse to follow
          a link into any of the others.
        </div>
      )}

      {scope.error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, fontSize: 12,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444',
        }}>{scope.error}</div>
      )}

      {d && (
        <>
          {(d.warnings || []).map((w, i) => (
            <div key={i} style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 8, fontSize: 12,
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b',
            }}>&#9888; {w}</div>
          ))}

          {allItems.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
              No scope items &mdash; add scope to this engagement before scanning.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
              {allItems.map(s => {
                const isStale = stale.some(x => x.value === s.value);
                const on = isSelected(s.id);
                return (
                  <label key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 6,
                    cursor: 'pointer', opacity: on ? 1 : 0.55,
                    background: !on ? 'var(--bg)' : (isStale ? 'rgba(239,68,68,0.06)' : 'var(--bg-input)'),
                    border: `1px ${on ? 'solid' : 'dashed'} ${on && isStale ? 'rgba(239,68,68,0.3)' : 'var(--border)'}`,
                  }}>
                    <input type="checkbox" checked={on} onChange={() => toggleScope(s.id)} />
                    <Badge>{s.scope_type}</Badge>
                    <code style={{ fontSize: 12, flex: 1, wordBreak: 'break-all' }}>{s.value}</code>
                    {s.description && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {s.description}
                      </span>
                    )}
                    {on && isStale && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', whiteSpace: 'nowrap' }}>
                        NOT ON APPLICATION
                      </span>
                    )}
                    {!on && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        EXCLUDED
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {missing.length > 0 && (
            <>
              <div style={sectionStyle}>
                On the application but NOT in this engagement &mdash; will not be tested ({missing.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 120, overflowY: 'auto' }}>
                {missing.map((s, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 6,
                    background: 'var(--bg)', border: '1px dashed var(--border)', opacity: 0.85,
                  }}>
                    <Badge>{s.scope_type}</Badge>
                    <code style={{ fontSize: 12, flex: 1, wordBreak: 'break-all', color: 'var(--text-secondary)' }}>{s.value}</code>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Engagement scope is copied from the application when the engagement is created and is
                not kept in sync. Update the engagement's Scope tab to test these.
              </div>
            </>
          )}
        </>
      )}

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginTop: 14, fontSize: 12,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444',
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 18 }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={launch} disabled={starting || scope.loading || !d?.canScan || targetCount === 0}>
          {starting ? 'Starting…'
            : targetCount === 0 ? 'Select a target to scan'
            : `Launch scan on ${targetCount} target${targetCount === 1 ? '' : 's'}`}
        </Button>
      </div>
    </Modal>
  );
}
